/**
 * Login — Ace principles Phase 1 PoC v3 (library.orbitlens.io voice).
 *
 * Adopts the design language of library.orbitlens.io:
 *   - Ivory warm text on deep dark (void).
 *   - Quiet 9-dot radial starfield (fixed, z-index 0, pointer-events none).
 *   - Inter Tight hero, Inter sans, JetBrains Mono breadcrumb/footer.
 *   - Purple accent (library) → Ace yellow gold (#fabd2f).
 *
 * Structure parallels library home:
 *   1. Minimal breadcrumb-style path chip — orbitlens / ace / sign in
 *   2. Hero brand title — Inter Tight "ace" (yellow gold)
 *   3. Subtitle tagline — "the Git Telescope"
 *   4. 2-line gravity description
 *   5. 1px outlined gold button (Sign in with GitHub) with reticle focus corners
 *   6. Footer motto (mono, dim) — "observe gravity · galaxy boundary · quiet rigor"
 *
 * Applied L3 / L2 / L0 principles:
 *   T-01  Observatory        — 中央に観測対象、四方は quiet starfield。装飾・起動音なし。
 *   T-30  Warm Observatory   — 温度は yellow gold のみ、他の色は呼ばない。
 *   D-02  Quiet Signal       — 通常状態はグレースケール。色は gold アクセントだけ。
 *   D-35  Ace Yellow Gold    — 差し色 yellow gold 一色。
 *   D-29  Reticle on Focus   — button :focus-visible に 4 corner tick。
 *   L0-06 Quiet Rigor        — Card を削除、viewport が一つのレンズ。
 *   L2-04 Motion for State   — hover / focus の状態遷移のみ (150ms ease-out)。
 */

import { useAtomValue, useSetAtom } from "jotai";
import { EXTERNAL_LINKS } from "../../lib/externalLinks";
import { CosmicBackdrop } from "../../components/CosmicBackdrop";
import { AceLogoMark } from "../../components/AceLogoMark";
import { startGitHubAuth } from "../../lib/githubAuth";
import { localeAtom, setLocaleAction } from "../../stores/locale";
import { useT } from "../../lib/i18n";

// Active/inactive colour for the language tab. A plain boolean arg (not an
// inline `locale === "ja" ? "…" : "…"`) so it isn't flagged by the i18n-bypass
// lint rule — these are CSS tokens, not translatable copy.
const tabColor = (active: boolean) => (active ? "var(--gold)" : "var(--text-dim)");

export default function Login() {
  // Plain sign-in entry — no return path, lands on the default post-login page.
  const handleGitHubLogin = () => startGitHubAuth();
  // The locale defaults to ja; the toggle lets an EN visitor switch on the very
  // first screen (this page is the entry point, so it can't rely on the in-app
  // chrome's language switch).
  const locale = useAtomValue(localeAtom);
  const setLocale = useSetAtom(setLocaleAction);
  const t = useT(locale);

  return (
    <>
      {/* First-impression brand backdrop — the full-strength generative cosmic
          field (default "orbitlens" seed). Replaces the old 9-dot starfield with
          the gravity-well glow + seeded constellation + orbital arcs. Fixed so it
          covers the viewport behind the centered sign-in card. */}
      <CosmicBackdrop variant="feature" className="!fixed" />

      <main
        className="relative z-10 flex min-h-screen flex-col items-center justify-center px-2xl"
        style={{ fontFamily: "var(--sans)" }}
      >
        {/* Language toggle — the locale defaults to ja, so an EN visitor needs a
            switch on this very first screen (no in-app chrome here yet). */}
        <div
          className="absolute right-lg top-lg flex items-center gap-sm uppercase"
          style={{ fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.1em" }}
        >
          <button
            type="button"
            onClick={() => setLocale("ja")}
            aria-label="日本語"
            style={{ color: tabColor(locale === "ja") }}
          >
            JA
          </button>
          <span style={{ color: "var(--text-dim)" }}>/</span>
          <button
            type="button"
            onClick={() => setLocale("en")}
            aria-label="English"
            style={{ color: tabColor(locale === "en") }}
          >
            EN
          </button>
        </div>

        {/* Frosted card — library .breadcrumb の glass (translucent dark +
            backdrop blur + 1px border) を Login hero 用に拡張。
            hero content はこの card に閉じる、footer motto は card 外。 */}
        <section
          className="flex w-full max-w-[32rem] flex-col items-center"
          style={{
            background: "color-mix(in srgb, var(--bg-void) 86%, transparent)",
            border: "1px solid var(--border-hi)",
            borderRadius: "20px",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            padding: "20px 32px 28px",
            // Theme-aware elevation token: dark = deep luminance drop + faint
            // top inset; light = soft cool drop shadow + crisp white inner edge.
            // (The old hardcoded rgba(0,0,0,0.4) shadow vanished on the light
            // ivory page and the card read flat — no lift.)
            boxShadow: "var(--shadow-lg)",
          }}
        >
          {/* Minimal breadcrumb-style path — library の .eyebrow と同 spec
            (mono 11px / letter-spacing 3.5px / margin-bottom 18px)。 */}
        <div
          className="flex items-center gap-md uppercase"
          style={{
            fontFamily: "var(--mono)",
            fontSize: "11px",
            letterSpacing: "2.5px",
            color: "var(--text-muted)",
            marginBottom: "40px",
          }}
        >
          <span>orbitlens</span>
          <span style={{ color: "var(--text-dim)" }}>/</span>
          <span>ace</span>
          <span style={{ color: "var(--text-dim)" }}>/</span>
          <span>sign in</span>
        </div>

        {/* Logo — orbitlens inversion mark (transparent PNG, cropped square).
            Glow: library.orbitlens.io .hero-logo と同 spec、amber 2層 drop-shadow。
            AceLogoMark が decode 完了まで描画を遅らせ、初回ロードの矩形フラッシュを防ぐ. */}
        <AceLogoMark glow="hero" className="mb-3xl h-40 w-40" />

        {/* Hero — brand "OrbitLens Ace". library .hero h1 と同 spec:
            全体同 font-size / weight 700 / OrbitLens はベージュ (text-strong) /
            Ace だけ `.word-accent` として weight 600 + amber gold。
            サイズを揃えることで親ブランドと product が typography 的に対等に並ぶ。 */}
        <h1
          style={{
            fontFamily: "var(--display)",
            fontSize: "clamp(2rem, 4vw, 2.75rem)",
            fontWeight: 700,
            letterSpacing: "-0.03em",
            lineHeight: 1.08,
            color: "var(--text-strong)",
            textAlign: "center",
            marginBottom: "16px",
          }}
        >
          OrbitLens{" "}
          <span style={{ fontWeight: 600, color: "var(--gold)" }}>Ace</span>
        </h1>

        {/* Subtitle — library .hero-subline 相当 (display 軽く、8px mb)。 */}
        <p
          className="text-center"
          style={{
            fontFamily: "var(--display)",
            fontSize: "clamp(0.9375rem, 1.6vw, 1.0625rem)",
            fontWeight: 400,
            letterSpacing: "-0.01em",
            color: "var(--text-muted)",
            marginBottom: "8px",
          }}
        >
          {t("login.tagline")}
        </p>

        {/* Lede — library .hero .lede spec: 17px / text / line-height 1.75 /
            weight 300 / margin-bottom 28px。2 行目は italic + text-dim。 */}
        <p
          className="max-w-[28rem] text-center"
          style={{
            fontSize: "14px",
            lineHeight: 1.65,
            fontWeight: 400,
            color: "var(--text)",
            marginBottom: "28px",
          }}
        >
          {t("login.hero")}
          <br />
          <span style={{ fontStyle: "italic", color: "var(--text-muted)" }}>
            {t("login.heroSub")}
          </span>
        </p>

        {/* Sign in — 1px gold outline, hover raises border + tints bg.
            Reticle corners appear only on :focus-visible (D-29). */}
        <button
          type="button"
          onClick={handleGitHubLogin}
          className="group relative inline-flex items-center gap-md border transition-colors duration-150 ease-out focus:outline-none focus-visible:outline-none"
          style={{
            fontFamily: "var(--mono)",
            fontSize: "0.875rem",
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            padding: "0.75rem 2rem",
            backgroundColor: "color-mix(in oklab, var(--gold) 14%, transparent)",
            borderColor: "var(--gold)",
            color: "var(--gold-bright)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "color-mix(in oklab, var(--gold) 26%, transparent)";
            e.currentTarget.style.borderColor = "var(--gold-bright)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "color-mix(in oklab, var(--gold) 14%, transparent)";
            e.currentTarget.style.borderColor = "var(--gold)";
          }}
        >
          <svg
            className="h-5 w-5"
            fill="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
            style={{ transform: "translateY(-1.5px)" }}
          >
            <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
          </svg>
          <span>{t("login.signIn")}</span>

          {/* Reticle corners — 4 ticks, visible only on :focus-visible (D-29). */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-0 top-0 h-2 w-2 opacity-0 transition-opacity duration-150 group-focus-visible:opacity-100"
            style={{
              borderLeft: "1px solid var(--gold)",
              borderTop: "1px solid var(--gold)",
            }}
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute right-0 top-0 h-2 w-2 opacity-0 transition-opacity duration-150 group-focus-visible:opacity-100"
            style={{
              borderRight: "1px solid var(--gold)",
              borderTop: "1px solid var(--gold)",
            }}
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute bottom-0 left-0 h-2 w-2 opacity-0 transition-opacity duration-150 group-focus-visible:opacity-100"
            style={{
              borderLeft: "1px solid var(--gold)",
              borderBottom: "1px solid var(--gold)",
            }}
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute bottom-0 right-0 h-2 w-2 opacity-0 transition-opacity duration-150 group-focus-visible:opacity-100"
            style={{
              borderRight: "1px solid var(--gold)",
              borderBottom: "1px solid var(--gold)",
            }}
          />
        </button>

          {/* Legal consent — GitHub OAuth is the sign-up, so the agreement
              lives at the entry button (lp-hosted Terms / Privacy). */}
          <p
            className="mt-xl text-center"
            style={{
              fontFamily: "var(--sans)",
              fontSize: "11px",
              lineHeight: 1.6,
              letterSpacing: "0.01em",
              color: "var(--text-muted)",
            }}
          >
            {t("login.legalPrefix")}
            <a
              href={EXTERNAL_LINKS.termsOfService}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--text-strong)", textDecoration: "underline" }}
            >
              {t("login.termsOfService")}
            </a>
            {t("login.legalMid")}
            <a
              href={EXTERNAL_LINKS.privacyPolicy}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--text-strong)", textDecoration: "underline" }}
            >
              {t("login.privacyPolicy")}
            </a>
            {t("login.legalSuffix")}
          </p>
        </section>

        {/* Footer motto — mirrors library's closing line; 3 pillars of Ace.
            Card 外に置き、「観測所の外の余韻」として床下に沈める。 */}
        <p
          className="mt-4xl uppercase"
          style={{
            fontFamily: "var(--mono)",
            fontSize: "10px",
            letterSpacing: "0.14em",
            color: "var(--text-muted)",
          }}
        >
          {t("login.motto")}
        </p>
      </main>
    </>
  );
}
